import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Check, Trash2, CheckSquare, DollarSign, FileText, Calendar, Repeat } from 'lucide-react';
import { useItemStore, useAuthStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import {
  isOccurrenceComplete,
  getDefaultOccurrenceStatus,
  getCompleteOccurrenceStatus,
  getDefaultStatus,
  getCompleteStatus,
  isCompleteStatus,
  isRecurringItem,
} from '@perfin/shared';
import type { Item, ItemType, Occurrence } from '@perfin/shared';

interface CalendarItemTileProps {
  item: Item;
  variant?: 'compact' | 'default' | 'expanded';
  showType?: boolean;
  isUnscheduled?: boolean; // Show with dashed border when placed by createdAt instead of dueDate
  className?: string;
  occurrenceDate?: Date;       // Which occurrence this tile represents (for recurring items)
  hideStatusToggle?: boolean;  // Hide tick/trash buttons (e.g., for All Recurring tab)
}

// Helper to check if two dates are the same day
function isSameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

function getItemTypeLabel(type: ItemType): string {
  switch (type) {
    case 'task': return 'Task';
    case 'transaction': return 'Transaction';
    case 'note': return 'Note';
    case 'keyDate': return 'Date';
    default: return 'Item';
  }
}

const TYPE_CONFIG = {
  task: {
    icon: CheckSquare,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    hoverBg: 'hover:bg-blue-50',
    borderColor: 'hover:border-blue-300',
    basePath: '/tasks',
  },
  transaction: {
    icon: DollarSign,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    hoverBg: 'hover:bg-green-50',
    borderColor: 'hover:border-green-300',
    basePath: '/transactions',
  },
  note: {
    icon: FileText,
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    hoverBg: 'hover:bg-amber-50',
    borderColor: 'hover:border-amber-300',
    basePath: '/notes',
  },
  keyDate: {
    icon: Calendar,
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    hoverBg: 'hover:bg-purple-50',
    borderColor: 'hover:border-purple-300',
    basePath: '/dates',
  },
};

// Priority badge colors (subdued/muted versions)
const PRIORITY_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  critical: { bg: 'bg-rose-800/80', text: 'text-rose-100', label: 'C' },
  high: { bg: 'bg-purple-700/80', text: 'text-purple-100', label: 'H' },
  medium: { bg: 'bg-amber-600/80', text: 'text-amber-100', label: 'M' },
  low: { bg: 'bg-gray-500/80', text: 'text-gray-100', label: 'L' },
  future: { bg: 'bg-sky-500/80', text: 'text-sky-100', label: 'F' },
};

function PriorityBadge({ priority, size = 'default' }: { priority?: string; size?: 'compact' | 'default' | 'inline' }) {
  if (!priority || !PRIORITY_CONFIG[priority]) return null;
  const config = PRIORITY_CONFIG[priority];

  if (size === 'compact') {
    // Small dot for compact view
    return (
      <span
        className={clsx('w-2 h-2 rounded-full flex-shrink-0', config.bg)}
        title={priority}
      />
    );
  }

  if (size === 'inline') {
    // Inline badge with letter (not absolute positioned)
    return (
      <span
        className={clsx(
          'w-4 h-4 rounded text-[10px] font-bold flex items-center justify-center flex-shrink-0',
          config.bg,
          config.text
        )}
        title={priority}
      >
        {config.label}
      </span>
    );
  }

  // Badge with letter for absolute positioning (legacy)
  return (
    <span
      className={clsx(
        'absolute top-0.5 right-0.5 w-4 h-4 rounded text-[10px] font-bold flex items-center justify-center',
        config.bg,
        config.text
      )}
      title={priority}
    >
      {config.label}
    </span>
  );
}

export function CalendarItemTile({
  item,
  variant = 'default',
  showType = false,
  isUnscheduled = false,
  className,
  occurrenceDate,
  hideStatusToggle,
}: CalendarItemTileProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { editItem, removeItem } = useItemStore();
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  const config = TYPE_CONFIG[item.type];
  const Icon = config.icon;
  const isRecurring = isRecurringItem(item);
  const occurrences = (item as unknown as { occurrences?: Occurrence[] }).occurrences || [];

  // Determine completion status (occurrence-aware for recurring items)
  const isComplete = (() => {
    if (isRecurring && occurrenceDate) {
      const occ = occurrences.find(o => {
        const occDate = o.date instanceof Date ? o.date : (o.date as unknown as { toDate: () => Date }).toDate();
        return isSameDay(occDate, occurrenceDate);
      });
      return occ ? isOccurrenceComplete(occ.status) : false;
    }
    const status = (item as unknown as { status?: string }).status;
    return isCompleteStatus(status);
  })();

  const handleClick = () => {
    navigate(`${config.basePath}/${item.id}`);
  };

  const handleToggleStatus = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;

    try {
      // For recurring items with occurrenceDate, update specific occurrence
      if (isRecurring && occurrenceDate) {
        const updatedOccurrences = [...occurrences];
        const occIndex = updatedOccurrences.findIndex(o => {
          const occDate = o.date instanceof Date ? o.date : (o.date as unknown as { toDate: () => Date }).toDate();
          return isSameDay(occDate, occurrenceDate);
        });

        if (occIndex >= 0) {
          if (isComplete) {
            updatedOccurrences[occIndex] = {
              ...updatedOccurrences[occIndex],
              status: getDefaultOccurrenceStatus(item.type),
              completedAt: undefined,
              finalizedAt: undefined,
            };
          } else {
            const now = new Date();
            updatedOccurrences[occIndex] = {
              ...updatedOccurrences[occIndex],
              status: getCompleteOccurrenceStatus(item.type),
              ...(item.type === 'task' ? { completedAt: now } : { finalizedAt: now }),
            };
          }
        } else {
          const now = new Date();
          updatedOccurrences.push({
            date: occurrenceDate,
            status: getCompleteOccurrenceStatus(item.type),
            ...(item.type === 'task' ? { completedAt: now } : { finalizedAt: now }),
          });
        }

        await editItem(item.createdBy, item.id, { occurrences: updatedOccurrences });
        toast.success(isComplete ? `${getItemTypeLabel(item.type)} reopened` : `${getItemTypeLabel(item.type)} completed!`);
        return;
      }

      // Non-recurring: update item-level status
      const currentStatus = (item as unknown as { status?: string }).status;
      const lastKnownStatus = (item as unknown as { lastKnownStatus?: string }).lastKnownStatus;

      if (isComplete) {
        const restoredStatus = lastKnownStatus || getDefaultStatus(item.type) || 'draft';
        await editItem(item.createdBy, item.id, { status: restoredStatus as 'new' | 'inProgress' | 'complete' | 'draft' | 'pending' | 'final', lastKnownStatus: undefined });
        toast.success(`${getItemTypeLabel(item.type)} reopened`);
      } else {
        const completeStatus = getCompleteStatus(item.type) as 'complete' | 'final';
        await editItem(item.createdBy, item.id, { status: completeStatus, lastKnownStatus: currentStatus });
        toast.success(`${getItemTypeLabel(item.type)} completed!`);
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const typeLabel = getItemTypeLabel(item.type);
    const isRecurring = item.recurrence && item.recurrence !== 'none';
    confirmDelete({
      title: isRecurring ? `Delete Recurring ${typeLabel}` : `Delete ${typeLabel}`,
      message: isRecurring
        ? <span>You are about to delete a Recurring {typeLabel}. This will permanently delete <strong>All Occurrences</strong> of the {typeLabel.toLowerCase()}, and it cannot be reversed. Do you wish to proceed?</span>
        : <span>You are about to permanently delete a single-instance {typeLabel}. Do you wish to proceed?</span>,
      onConfirm: async () => {
        if (!user) return;
        try {
          await removeItem(item.createdBy, item.id);
          toast.success(`${typeLabel} deleted`);
        } catch {
          toast.error('Failed to delete');
        }
      },
    });
  };

  if (variant === 'compact') {
    return (
      <div
        className={clsx(
          'w-full px-1 py-0.5 text-xs rounded transition-colors flex items-center gap-1',
          config.bgColor,
          config.textColor,
          config.hoverBg,
          isUnscheduled && 'border border-dashed border-gray-400',
          isComplete && 'opacity-60',
          className
        )}
      >
        <button onClick={handleClick} className="flex items-center gap-1 flex-1 min-w-0 text-left">
          {showType && <Icon className="h-3 w-3 flex-shrink-0" />}
          <span className={clsx('truncate flex-1', isComplete && 'line-through')}>{item.name}</span>
        </button>
        {isRecurring && occurrenceDate && (
          <span title="Recurring"><Repeat className="h-3 w-3 flex-shrink-0 text-gray-500" /></span>
        )}
        <PriorityBadge priority={item.priority} size="compact" />
        {!hideStatusToggle && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              onClick={handleToggleStatus}
              className="p-0.5 rounded hover:bg-black/10"
              title={isComplete ? 'Mark incomplete' : 'Mark complete'}
            >
              <Check className={clsx('h-3 w-3', isComplete ? 'text-green-600' : 'text-current opacity-50')} strokeWidth={3} />
            </button>
            <button
              onClick={handleDelete}
              className="p-0.5 rounded hover:bg-red-100 hover:text-red-600"
              title="Delete"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    );
  }

  if (variant === 'expanded') {
    return (
      <div
        className={clsx(
          'w-full p-3 rounded-lg border transition-colors',
          isUnscheduled ? 'border-dashed border-gray-400' : 'border-gray-200',
          config.hoverBg,
          config.borderColor,
          isComplete && 'opacity-60',
          className
        )}
      >
        <div className="flex items-start gap-2">
          {showType && (
            <div className={clsx('p-1 rounded flex-shrink-0', config.bgColor)}>
              <Icon className={clsx('h-4 w-4', config.textColor)} />
            </div>
          )}
          <button onClick={handleClick} className="flex-1 min-w-0 text-left">
            <div className={clsx('font-medium text-gray-900', isComplete && 'line-through')}>{item.name}</div>
            {item.description && (
              <div className="text-sm text-gray-500 mt-1 truncate">{item.description}</div>
            )}
          </button>
          {!hideStatusToggle && (
            <div className="flex items-center gap-1 flex-shrink-0">
              {isRecurring && occurrenceDate && (
                <span title="Recurring"><Repeat className="h-4 w-4 flex-shrink-0 text-gray-500" /></span>
              )}
              <PriorityBadge priority={item.priority} size="inline" />
              <button
                onClick={handleToggleStatus}
                className="p-1 rounded hover:bg-gray-100"
                title={isComplete ? 'Mark incomplete' : 'Mark complete'}
              >
                <Check className={clsx('h-4 w-4', isComplete ? 'text-green-500' : 'text-gray-400')} strokeWidth={3} />
              </button>
              <button
                onClick={handleDelete}
                className="p-1 rounded hover:bg-red-50 hover:text-red-600 text-gray-400"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div
      className={clsx(
        'w-full p-2 text-xs rounded bg-white border transition-colors',
        isUnscheduled ? 'border-dashed border-gray-400' : 'border-gray-200',
        config.hoverBg,
        config.borderColor,
        isComplete && 'opacity-60',
        className
      )}
    >
      <div className="flex items-center gap-1">
        <button onClick={handleClick} className="flex items-center gap-1 flex-1 min-w-0 text-left">
          {showType && <Icon className="h-3 w-3 flex-shrink-0" />}
          <span className={clsx('line-clamp-2', isComplete && 'line-through')}>{item.name}</span>
        </button>
        {!hideStatusToggle && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {isRecurring && occurrenceDate && (
              <span title="Recurring"><Repeat className="h-3 w-3 flex-shrink-0 text-gray-500" /></span>
            )}
            <PriorityBadge priority={item.priority} size="inline" />
            <button
              onClick={handleToggleStatus}
              className="p-0.5 rounded hover:bg-gray-100"
              title={isComplete ? 'Mark incomplete' : 'Mark complete'}
            >
              <Check className={clsx('h-3 w-3', isComplete ? 'text-green-500' : 'text-gray-400')} strokeWidth={3} />
            </button>
            <button
              onClick={handleDelete}
              className="p-0.5 rounded hover:bg-red-50 hover:text-red-600 text-gray-400"
              title="Delete"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
